import * as React from "react";
import { $, merge, media } from "glamor";
import { formatMessage } from "lib/globalize-util";
import { List, Map } from "immutable";
import theme from "styles/theme";
import { ProductDisplayFields } from "components/product-collection/product-collection-views/product-collection-item-modal";
import FlatButton from "material-ui/FlatButton";
import CartIcon from "components/icons/assortments/cart";
import { ColorSwatches } from "./color-swatches";
import { ProductDetails as ItemDetails } from "../product-collection-item-modal/product-details";
import EditPageIcon from "components/icons/edit-page";
import classNames from "classnames";
import { Item as ProductDisplayFieldPaths } from "constants/product-collection";
import { IDisplayFields, getVariantColorIds, formatLightboxMedia } from "helpers/product-collection";
import { DATATEST } from "constants/integration";
import { ColorVariants } from "./color-variants";
import { HeroImage } from "./hero-image";
import Image360Icon from "components/icons/image-360";
import VideoPlayIcon from "components/icons/video-play";
import IconButton from "material-ui/IconButton";
import { MEDIA_TYPE_360, MEDIA_TYPE_VIDEO } from "constants/media";
import { MediaLightBox } from "components/product-media/media-lightbox";
import { ICollectionSwatch } from "constants/color-swatches";
import { getProductHeroImage, getProductImages } from "./helpers";
import {
    getProductShopViewFieldsFallback,
    getProductShopViewFieldsForDetail,
    IShopViewFields,
} from "helpers/collection";

interface IProps {
    merchantId: string;
    productId: string;
    onFetchGlobalProducts: (params) => void;
    globalProducts: List<any>;
    globalProductsIsFetching?: boolean;
    onSelectColorVariant: (prod: { _id: string }) => void;
    router: any;
    inAssortmentProducts: { [key: string]: string[] };
    onAddItem: (prod: { _id: string }) => void;
    itemDisplayProperty: List<any>;
    brandEntities: any;
    editable: boolean;
    shopViewFields: IDisplayFields;
    readOnly?: boolean;
}

export const ProductDetails: React.FunctionComponent<IProps> = ({
    merchantId,
    productId,
    onFetchGlobalProducts,
    globalProducts,
    globalProductsIsFetching,
    onSelectColorVariant,
    router,
    inAssortmentProducts,
    onAddItem,
    itemDisplayProperty,
    brandEntities,
    editable,
    shopViewFields,
    readOnly,
}) => {
    const [product, setProduct] = React.useState<Map<any, any>>(Map());
    const [hoverHeroImage, setHoverHeroImage] = React.useState<string>();
    const [currentHeroImage, setCurrentHeroImage] = React.useState<string>();
    const [lightboxIsOpen, setLightboxIsOpen] = React.useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = React.useState<number>(0);

    const setFilters = () => {
        const filter = [
            {
                eq: {
                    field: "_product_id",
                    value: {
                        string_value: productId,
                    },
                },
            },
        ];

        return filter;
    };

    React.useEffect(() => {
        if (globalProducts && globalProducts.size) {
            const _product = globalProducts.find(
                (prod) => prod.getIn(ProductDisplayFieldPaths.FIELDS.PRODUCT_ID) === productId
            );
            if (_product) {
                const brand = brandEntities[_product.getIn(ProductDisplayFieldPaths.FIELDS.BRAND_ID)];
                setCurrentHeroImage(getProductHeroImage(brand, _product));
                setProduct(_product);
            } else {
                onFetchGlobalProducts({
                    filter: setFilters(),
                    variants: true,
                    modal: true,
                    merchantId,
                });
            }
        } else {
            onFetchGlobalProducts({
                filter: setFilters(),
                variants: true,
                modal: true,
                merchantId,
            });
        }
    }, [brandEntities, productId, globalProducts]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => setSelectedIndex(0), [product]);

    const styles = React.useMemo(() => {
        return merge(
            $(" .heroImageContainer", {
                width: "415px",
                height: "648px",
                position: "relative",
                margin: "0 16px",
            }),
            $(" .heroSwatch", {
                height: "84px",
                width: "84px",
                borderRadius: "50%",
                border: "solid 2px #c1c1c1",
                position: "absolute",
                bottom: "0px",
                right: "0px",
                backgroundSize: "cover",
            }),
            $(" .imagesCarousel", {
                width: "70px",
            }),
            $(" .imagesCarousel div", {
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                cursor: "pointer",
                height: "110px",
                width: "70px",
                marginBottom: "16px",
            }),
            $(" .statedCarouselImage", {
                borderBottom: `3px ${theme.palette.accent4Color} solid`,
            }),
            $(" .heroImageContainer", {
                width: "415px",
                height: "648px",
                position: "relative",
            }),
            $(" .heroImage", {
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                cursor: "zoom-in",
                height: "624px",
                width: "395px",
                backgroundSize: "contain",
            }),
            $(" .heroImageMobile", {
                display: "none",
                width: "100%",
            }),
            $(" .leftSection", {
                display: "flex",
                flex: 1,
                flexDirection: "row-reverse",
            }),
            $(" .rightSection", {
                flex: 1,
                minWidth: "320px",
            }),
            $(" .colorSwatch", {
                display: "none !important",
            }),
            $(" .color", {
                paddingLeft: "0px !important",
            }),
            $(" .buttonContainer", {
                padding: "20px 0",
            }),
            $(" .productName", {
                textDecoration: "none !important",
            }),
            $(" .zoomButtonContainer", {
                position: "absolute",
                backgroundColor: "#fff",
                bottom: "36px",
                left: "16px",
                borderRadius: "50%",
                boxShadow: "3px 3px 20px 0px rgba(45,45,45,0.3)",
            }),
            $(" .zoomButton svg", {
                height: "20px !important",
                width: "20px !important",
            }),
            $(" .productDetails", {
                width: "100% !important",
                padding: "0 160px !important",
            }),
            $(" span.fieldValue", {
                fontSize: "14px !important",
                padding: "10px !important",
            }),
            $(" .fieldContainer .DraftEditor-root:hover", {
                background: "white !important",
            }),
            $(" .pdpContent", {
                display: "flex",
                paddingBottom: "48px",
            }),
            $(" .hasColorVariants .color", {
                display: "none !important",
            }),
            $(" .mediaButton", {
                width: "36px !important",
                height: "36px !important",
                padding: "0px !important",
                backgroundColor: `${theme.palette.lighterGrey} !important`,
                borderRadius: "50% !important",
                color: `${theme.palette.darkGrey} !important`,
                marginRight: "10px !important",
            }),
            $(" .mediaIcon", {
                width: "30px",
                height: "30px",
            }),
            media(
                "(max-width: 780px)",
                $(" .pdpContent", {
                    display: "block",
                }),
                $(" .leftSection", {
                    flexDirection: "column",
                }),
                $(" .heroImageContainer", {
                    margin: 0,
                    width: "100%",
                    height: "auto",
                }),
                $(" .heroImage", {
                    display: "none",
                }),
                $(" .heroImageMobile", {
                    display: "block",
                }),
                $(" .imagesCarousel", {
                    width: "100%",
                    overflow: "auto",
                }),
                $(" .imagesCarousel > div", {
                    display: "inline-block",
                    marginRight: "8px",
                }),
                $(" .imagesCarousel > div:last-child", {
                    display: "inline-block",
                    marginRight: 0,
                }),
                $(" .productDetails", {
                    padding: "0 0 !important",
                })
            )
        );
    }, []);

    const muiStyles = React.useMemo(() => {
        return {
            addToAssortment: {
                width: "268px",
                height: "54px",
                borderRadius: "27px",
                border: "solid 2px #1fc8bf",
                backgroundColor: "#1fc8bf",
            },
            addToAssortmentLabel: {
                color: "white",
                fontSize: "16px",
            },
            detailsButtonLabelStyle: {
                textDecoration: "underline",
            },
        };
    }, []);

    const brandId = product && product.getIn(ProductDisplayFieldPaths.FIELDS.BRAND_ID);
    const brand = brandEntities[brandId];

    const images = getProductImages(brand, product, 1200);

    const variantColors = product.getIn(ProductDisplayFieldPaths.FIELDS.VARIANT_COLORS, "");
    const variantColorIds = getVariantColorIds(variantColors);
    const productMedia = product?.getIn(ProductDisplayFieldPaths.FIELDS.MEDIA)?.split(",") || []; // eslint-disable-line react-hooks/exhaustive-deps

    const productShopView: IShopViewFields = React.useMemo(
        () =>
            getProductShopViewFieldsForDetail(itemDisplayProperty, shopViewFields, product) ||
            getProductShopViewFieldsFallback(),
        [product, itemDisplayProperty, shopViewFields]
    );

    const inAssortment = inAssortmentProducts[productId];

    const colorSwatches = React.useMemo<{
        colorSwatches: { [key: string]: ICollectionSwatch };
        heroSwatch?: ICollectionSwatch;
    }>(() => {
        let swatches = product.getIn(ProductDisplayFieldPaths.FIELDS.COLOR_SWATCHES);
        const _productId = product.getIn(ProductDisplayFieldPaths.FIELDS.PRODUCT_ID);
        if (swatches) {
            swatches = swatches.split(",");
            const _colorSwatches = {};
            swatches.forEach((swatch) => {
                const [color, swatch_type, value, variantRef, label] = swatch.split("&&");
                _colorSwatches[variantRef] = { color, swatch_type, value, label };
            });
            return {
                colorSwatches: _colorSwatches,
                heroSwatch: _colorSwatches[_productId],
            };
        } else {
            return { colorSwatches: {} };
        }
    }, [product]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const lightboxMedia = React.useMemo(() => formatLightboxMedia(images, productMedia), [product]);

    const has360 = React.useMemo(
        () => lightboxMedia.findIndex((lightboxMediaEntry) => lightboxMediaEntry.type === MEDIA_TYPE_360),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [product]
    );

    const hasVideo = React.useMemo(
        () => lightboxMedia.findIndex((lightboxMediaEntry) => lightboxMediaEntry.type === MEDIA_TYPE_VIDEO),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [product]
    );

    const handleImageEnter = React.useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const _hoverHeroImage = event.currentTarget.getAttribute("data-image");
        setHoverHeroImage(_hoverHeroImage);
    }, []);

    const handleImageLeave = React.useCallback(() => {
        setHoverHeroImage(null);
    }, []);

    const handleClickAdditionalImage = React.useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const _heroImage = event.currentTarget.getAttribute("data-image");
        const _heroImageIndex = event.currentTarget.getAttribute("data-index");
        setCurrentHeroImage(_heroImage);
        setSelectedIndex(parseInt(_heroImageIndex, 10));
    }, []);

    const handleSelectColorSwatch = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const _productId = event.currentTarget.getAttribute("data-productid");
            onSelectColorVariant({ _id: _productId });
        },
        [onSelectColorVariant]
    );

    const handleViewInAssortment = React.useCallback(() => {
        const itemInAssortment = inAssortmentProducts[productId] && inAssortmentProducts[productId][0];
        const { query } = router.location;
        delete query.productId;
        router.replace({
            pathname: router.location.pathname,
            query: {
                ...query,
                itemId: itemInAssortment,
                mode: "cart",
            },
        });
    }, [router, inAssortmentProducts, productId]);

    const handleAddItem = React.useCallback(() => {
        onAddItem({ _id: productId });
    }, [productId, onAddItem]);

    const handleOpenLightbox = React.useCallback(() => {
        setLightboxIsOpen(true);
    }, []);

    const handleCloseLightbox = React.useCallback(() => {
        setLightboxIsOpen(false);
    }, []);

    const handleSelectMedia = React.useCallback(
        ({ currentTarget }) => {
            const dataMediaIndex = currentTarget.getAttribute("data-index");
            setSelectedIndex(parseInt(dataMediaIndex, 10));
            setLightboxIsOpen(true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [product]
    );

    return (
        <div {...styles}>
            <div data-test={DATATEST.ASSORTMENTS.PDP.PRODUCT_DETAILS_CONTAINER} className="pdpContent">
                <div>
                    <div className="leftSection">
                        <HeroImage
                            hoverHeroImage={hoverHeroImage}
                            currentHeroImage={currentHeroImage}
                            onOpenLightbox={handleOpenLightbox}
                            heroSwatch={colorSwatches?.heroSwatch}
                        />
                        <div className="imagesCarousel">
                            {images.map((url, index) => (
                                <div
                                    key={url}
                                    style={{ backgroundImage: `url(${url})` }}
                                    onMouseEnter={handleImageEnter}
                                    onMouseLeave={handleImageLeave}
                                    data-image={url}
                                    data-index={index}
                                    onClick={handleClickAdditionalImage}
                                    className={classNames({ statedCarouselImage: url === currentHeroImage })}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        {has360 >= 0 && (
                            <IconButton className="mediaButton" data-index={has360} onClick={handleSelectMedia}>
                                <Image360Icon className="mediaIcon" />
                            </IconButton>
                        )}
                        {hasVideo >= 0 && (
                            <IconButton className="mediaButton" data-index={hasVideo} onClick={handleSelectMedia}>
                                <VideoPlayIcon className="mediaIcon" />
                            </IconButton>
                        )}
                    </div>
                </div>
                <div className={classNames("rightSection", { hasColorVariants: variantColorIds.length > 1 })}>
                    <ProductDisplayFields
                        brandNameOverride={productShopView.brandName}
                        productName={productShopView.title || formatMessage("Loading...")}
                        styleNumber={productShopView.subTitle}
                        color={productShopView.color}
                        wholesalePrice={productShopView.costPrice}
                        retailPrice={productShopView.retailPrice}
                        brandId={brandId}
                        productId={productId}
                        router={router}
                        brandEntities={brandEntities}
                        showBrandIcon={true}
                    />
                    {!readOnly && !variantColorIds.length && (
                        <ColorSwatches
                            colorSwatches={colorSwatches.colorSwatches}
                            onSelectColorSwatch={handleSelectColorSwatch}
                            productId={productId}
                        />
                    )}
                    {!readOnly && variantColorIds.length > 1 && (
                        <ColorVariants
                            brand={brand}
                            productId={productId}
                            variantColorIds={variantColorIds}
                            globalProducts={globalProducts}
                            onSelectColorVariant={onSelectColorVariant}
                            colorSwatches={colorSwatches.colorSwatches}
                        />
                    )}
                    <div className="buttonContainer">
                        {!inAssortment && editable && (
                            <FlatButton
                                label={formatMessage("Add to Assortment")}
                                style={muiStyles.addToAssortment}
                                labelStyle={muiStyles.addToAssortmentLabel}
                                icon={<CartIcon color={theme.palette.canvasColor} fillInBasket={inAssortment} />}
                                onClick={handleAddItem}
                                data-test={DATATEST.ASSORTMENTS.PDP.ADD_TO_ASSORTMENT}
                            />
                        )}
                        {!readOnly && inAssortment && (
                            <FlatButton
                                label={
                                    editable ? formatMessage("Edit in Assortment") : formatMessage("View in Assortment")
                                }
                                icon={<EditPageIcon color={"#1fc8bf"} />}
                                labelStyle={muiStyles.detailsButtonLabelStyle}
                                onClick={handleViewInAssortment}
                                data-test={DATATEST.ASSORTMENTS.PDP.EDIT_IN_ASSORTMENT}
                            />
                        )}
                    </div>
                </div>
            </div>
            <ItemDetails
                itemsId={""}
                item={product}
                items={List()}
                itemId={""}
                readOnly={true}
                schema={itemDisplayProperty}
                allowedValueFields={[]}
            />
            {lightboxIsOpen && (
                <MediaLightBox
                    open={lightboxIsOpen}
                    media={lightboxMedia}
                    currentIndex={selectedIndex}
                    onClose={handleCloseLightbox}
                    onViewChange={setSelectedIndex}
                />
            )}
        </div>
    );
};
